import { toast } from "@/hooks/use-toast";
import { navigate } from "gatsby";
import jwtDecode from "jwt-decode";
import React, { createContext, useContext, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useLocalStorage } from "../../hooks/use-local-storage";
import { AuthAPI } from "../../services/auth.service";
import { api } from "../../services/config/axios";
import {
  AuthProps,
  AuthenticationContextType,
  RegistrationValues,
  User,
} from "../../types/auth.type";

const AuthContext = createContext<AuthenticationContextType | null>(null);

export default AuthContext;

export const useAuth = () => {
  return useContext(AuthContext) as AuthenticationContextType;
};

interface LoginParams {
  username: string;
  password: string;
}

export const AuthProvider: React.FC<AuthProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [access, setAccess] = useLocalStorage<string | null>("access", null);
  const [refresh, setRefresh] = useLocalStorage<string | null>("refresh", null);
  const queryClient = useQueryClient();

  const getInitials = () => {
    return `${user?.first_name ? user.first_name[0] : "?"}${
      user?.last_name ? user.last_name[0] : ""
    }`;
  };

  const { data: user, isLoading: isLoadingUser } = useQuery<User>({
    queryKey: ["user"],
    queryFn: async () => {
      if (!access) {
        throw new Error("No access token available");
      }
      const response = await AuthAPI.getUser();
      return response;
    },
    enabled: !!access,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    api.defaults.headers.common["Authorization"] = access
      ? `Bearer ${access}`
      : null;
    queryClient.invalidateQueries();
    // if (access && !user) {
    //   setUser(jwtDecode(access));
    // }
  }, [access]);

  const refreshMutation = useMutation({
    mutationFn: async (refresh: string) => await AuthAPI.refresh(refresh),
    onSuccess(data, variables, context) {
      // console.info("Refresh token success", data);
      setAccess(data.access);
      setRefresh(data.refresh);
    },
    onError(error, variables, context) {
      toast({
        variant: "destructive",
        title: "Session expired",
        description: "Please login again.",
      });
      // setUser(undefined);
      setAccess(null);
      setRefresh(null);
    },
  });

  const refreshUser = async () => {
    if (!refresh) {
      return;
    }
    await refreshMutation.mutateAsync(refresh);
  };

  const loginMutation = useMutation({
    mutationFn: async (params: LoginParams) =>
      await AuthAPI.login(params.username, params.password),
    onSuccess(data, variables, context) {
      setAccess(data.access);
      setRefresh(data.refresh);
      navigate("/dashboard");
    },
    onError(error, variables, context) {
      toast({
        variant: "destructive",
        title: "Login failed",
        description: "Please check your username and password.",
      });
      // setUser(undefined);
      setAccess(null);
      setRefresh(null);
    },
  });

  const loginUser = async (username: string, password: string) => {
    await loginMutation.mutateAsync({ username: username.toLowerCase(), password });
  };

  const logoutMutation = useMutation({
    mutationFn: async (refresh: string) => await AuthAPI.logout(refresh),
    onMutate() {
      // setUser(undefined);
      setAccess(null);
      setRefresh(null);
    },
    onError(error, variables, context) {
      toast({
        variant: "destructive",
        title: "Logout failed",
        description: "Please try again.",
      });
    },
    onSettled(data, error, variables, context) {
      toast({
        title: "Logout successful",
        description: "You have been logged out.",
      });
    },
  });

  const logoutUser = async () => {
    if (!refresh) {
      setAccess(null);
      setRefresh(null);
      navigate("/", { replace: true });
      return;
    }

    await logoutMutation.mutateAsync(refresh);
    navigate("/", { replace: true });
  };

  const registerMutation = useMutation({
    mutationFn: async (details: RegistrationValues) =>
      await AuthAPI.register(details),
    onError(error, variables, context) {
      toast({
        variant: "destructive",
        title: "Registration failed",
        description: "Please check your details and try again.",
      });
      // setUser(undefined);
      setAccess(null);
      setRefresh(null);
    },
    onSettled(data, error, variables, context) {
      toast({
        title: "Registration successful",
        description: "You can now log in.",
      });
      navigate("/login");
    },
  });

  const registerUser = async (details: RegistrationValues) => {
    await registerMutation.mutateAsync({ ...details, username: details.username.toLowerCase() });
    navigate("/login");
  };

  const updateUserMutation = useMutation({
    mutationFn: async (userData: Partial<User>) =>
      await AuthAPI.updateUser(userData),
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries("user");
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError(error, variables, context) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "Please try again later.",
      });
    },
  });

  const updateUser = async (userData: Partial<User>) => {
    await updateUserMutation.mutateAsync(userData);
  };

  React.useEffect(() => {
    setLoading(
      [
        registerMutation.isLoading,
        refreshMutation.isLoading,
        loginMutation.isLoading,
        logoutMutation.isLoading,
        isLoadingUser,
      ].some((e) => e)
    );
  }, [registerMutation, refreshMutation, loginMutation, logoutMutation]);

  return (
    <AuthContext.Provider
      value={{
        user,
        access,
        refresh,
        loading,
        // setUser,
        registerUser,
        registerMutation,
        loginUser,
        loginMutation,
        logoutUser,
        logoutMutation,
        refreshUser,
        refreshMutation,
        updateUser,
        updateUserMutation,
        getInitials,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
